import { PhoneInput } from "@eisox/design-system";
import { z } from "@eisox/zod";

import { m } from "~/paraglide/messages";

export const nameRegExp = /^[a-zA-ZÀ-ÖÙ-öù-ÿĀ-žḀ-ỿ0-9\s-]+$/;

export const accountSchema = z.object({
  lastName: z
    .string()
    .regex(nameRegExp, { error: m.enter_valid({ label: m.last_name() }) })
    .nonempty()
    .max(40),
  firstName: z
    .string()
    .regex(nameRegExp, { error: m.enter_valid({ label: m.first_name() }) })
    .nonempty()
    .max(40),
  company: z.string().nonempty(),
  phoneNumber: z.string().refine(value => !value || PhoneInput.isValidPhoneNumber(value), {
    error: m.enter_valid({ label: m.phone() }),
  }),
});

export type AccountSchemaType = z.infer<typeof accountSchema>;

export const emailSchema = z.object({
  email: z.email(),
});

export enum PasswordRules {
  MIN_LENGTH = "minLength",
  UPPER_CASE = "upperCase",
  LOWER_CASE = "lowerCase",
  NUMBER = "number",
  SPECIAL_CHARACTER = "specialCharacter",
}

export const passwordSchema = z
  .string()
  .regex(/^.{12,}$/, { error: PasswordRules.MIN_LENGTH })
  .regex(/[A-Z]/, { error: PasswordRules.UPPER_CASE })
  .regex(/[a-z]/, { error: PasswordRules.LOWER_CASE })
  .regex(/[0-9]/, { error: PasswordRules.NUMBER })
  .regex(/[@#$%()[\]{}*+\-_.,:;<>=|&~?!]/, { error: PasswordRules.SPECIAL_CHARACTER });

export const resetPasswordSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine(data => data.password === data.confirmPassword, {
    error: m.passwords_do_not_match(),
    path: ["confirmPassword"],
  });

export const enrollAccountSchema = accountSchema
  .extend({
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine(data => data.password === data.confirmPassword, {
    error: m.passwords_do_not_match(),
    path: ["confirmPassword"],
  });

export type EnrollAccountSchemaType = z.infer<typeof enrollAccountSchema>;

export const changeEmailSchema = emailSchema.extend({ password: z.string().nonempty() });

export type ChangeEmailSchemaType = z.infer<typeof changeEmailSchema>;
